import { get as getCookie } from 'js-cookie';
import React, { useEffect, useState } from 'react';
import { Control, UseFormReset, useForm } from 'react-hook-form';
import useSWR from 'swr';

import { useLoggedInEmployeeProfile } from '~/components/Auth';
import { LEGACY_COOKIE } from '~/data/cookies/';
import { useEmployee } from '~/data/employee';
import { useAllEnums } from '~/data/enums';
import {
  getCountryByLegalEntity,
  useAllLegalEntities,
} from '~/data/legalEntity';
import { Roles } from '~/data/user';
import { useSelectedEmployeeId } from '~/hooks/employee';
import {
  EditCustomField,
  ReadCustomField,
  jsEditCustomFieldData,
  readCustomData,
} from '~/legacy-components/CustomDataFields';
import { getCurrentLanguage } from '~/localization';

import { AnswerPreProcess } from '../../../types';

import {
  getCustomEmployeeFieldAttributes,
  getCustomEmployeeFieldLanguages,
  getCustomEmployeeFields,
  saveCustomFieldValues,
} from './data';

export type UseCustomDataOutput = {
  isLoading: boolean;
  error: string;
  canEdit: boolean;
  readFieldData: ReadCustomField[];
  editFieldData: EditCustomField[];
  handleFormSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  control: Control<AnswerPreProcess>;
  reset: UseFormReset<AnswerPreProcess>;
  isSubmitSuccessful: boolean;
  submitCount: number;
};

export const useCustomData = (empId: string): UseCustomDataOutput => {
  const userProfile = useLoggedInEmployeeProfile();
  const [error, setError] = useState<string>('');
  const [readFieldData, setReadFieldData] = useState<ReadCustomField[]>([]);
  const [editFieldData, setEditFieldData] = useState<EditCustomField[]>([]);
  const {
    isSelectedEmployeeLoggedInUser,
    selectedEmployeeDetails: { hasRoleAccessOverEmployee },
  } = useSelectedEmployeeId();

  const currentLanguage = getCurrentLanguage();
  const { data: customEmployeeFieldsRes, isLoading: employeeFieldsLoading } =
    useSWR(['customEmployeeFields', empId], () =>
      getCustomEmployeeFields(empId),
    );
  const {
    data: customEmployeeFieldLanguagesRes,
    isLoading: employeeFieldLanguageLoading,
  } = useSWR(['customEmployeeFieldLanguages', currentLanguage], () =>
    getCustomEmployeeFieldLanguages(currentLanguage),
  );
  const {
    data: customEmployeeFieldAttributesRes,
    isLoading: fieldAttributesLoading,
  } = useSWR('customEmployeeFieldsFieldAttributes', () =>
    getCustomEmployeeFieldAttributes(),
  );
  const {
    legalEntities,
    isLoading: legalEntitiesLoading,
    hasData: legalEntityHasData,
  } = useAllLegalEntities();
  const isLoading =
    fieldAttributesLoading ||
    employeeFieldsLoading ||
    employeeFieldLanguageLoading ||
    (legalEntitiesLoading && !legalEntityHasData);
  const { allEnums } = useAllEnums();
  const iRehire = getCookie(LEGACY_COOKIE.REHIRE) === 'active' ? true : false;
  const allowedRoles: Roles[] = [
    Roles.HR_STAFF,
    Roles.HR_SALARY,
    Roles.HR_PEOP,
    Roles.HR_PEOP_DEV,
    Roles.HR_DEV,
    Roles.HR_ADMIN,
    Roles.MANAGER,
    Roles.ALT_MANAGER,
    Roles.DEPUTY_MANAGER,
  ];

  const {
    employee: { empBasicData, empPosition, empPositionPending },
  } = useEmployee();

  const legalEntityId =
    ['NEW', 'PENDING'].includes(empBasicData.fEmpStatus) && empPositionPending
      ? empPositionPending.fEmpLegalEntity
      : empPosition.fEmpLegalEntity;

  const canEdit: boolean =
    isSelectedEmployeeLoggedInUser || hasRoleAccessOverEmployee(allowedRoles);

  const {
    handleSubmit,
    formState: { isSubmitSuccessful, submitCount },
    control,
    reset,
  } = useForm<AnswerPreProcess>();
  const handleFormSubmit = React.useMemo(() => {
    return handleSubmit(async (data) => {
      await saveCustomFieldValues(empId, data);
    });
  }, [empId, handleSubmit]);

  useEffect(() => {
    try {
      if (!isLoading) {
        let hashedFields = Object.assign(
          {},
          ...customEmployeeFieldAttributesRes!.map((field) => ({
            [field.fCustomDataAttributeName]: field,
          })),
        );
        const legalEntityCountryForUser = getCountryByLegalEntity(
          legalEntityId,
          legalEntities,
        );
        if (!iRehire) {
          setReadFieldData(
            readCustomData(
              'EMPLOYEE',
              hashedFields,
              customEmployeeFieldsRes!,
              legalEntityCountryForUser,
              allEnums,
            ),
          );
          setEditFieldData(
            jsEditCustomFieldData({
              strEntity: 'EMPLOYEE',
              customFieldAttributesHash: hashedFields,
              customFields: customEmployeeFieldsRes!,
              legalEntityCountry: legalEntityCountryForUser,
              allEnums,
            }),
          );
        }
      }
    } catch (err: unknown) {
      setError(`${err}`);
    }
  }, [
    allEnums,
    customEmployeeFieldAttributesRes,
    customEmployeeFieldLanguagesRes,
    customEmployeeFieldsRes,
    iRehire,
    isLoading,
    legalEntities,
    legalEntityId,
    userProfile.legalEntity.id,
  ]);

  return {
    reset,
    isLoading,
    error,
    canEdit,
    readFieldData,
    editFieldData,
    handleFormSubmit,
    control,
    isSubmitSuccessful,
    submitCount,
  };
};
